import { ButtonHTMLAttributes } from 'react';
import Spinner from './Spinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
}

export default function Button({
  children,
  variant = 'primary',
  size = 'sm',
  fullWidth = false,
  className = '',
  disabled,
  loading = false,
  ...props
}: ButtonProps) {
  const baseStyles = 'rounded-lg font-normal transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center active:scale-[0.98]';

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-5 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const variantStyles = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 hover:scale-[1.02] focus:ring-primary-500 shadow-soft hover:shadow-soft-lg border border-transparent',
    secondary: 'bg-white text-neutral-700 hover:bg-neutral-50 hover:border-primary-300 hover:scale-[1.01] border border-neutral-200 focus:ring-neutral-300 shadow-soft hover:shadow-soft-lg',
    ghost: 'bg-transparent text-neutral-700 hover:bg-neutral-100 focus:ring-neutral-300 border border-transparent',
    danger: 'bg-red-600 text-white hover:bg-red-700 hover:scale-[1.02] focus:ring-red-500 shadow-soft hover:shadow-soft-lg border border-transparent',
    'danger-outline': 'bg-white text-error-600 hover:bg-error-50 hover:border-error-300 hover:scale-[1.01] border border-error-200 focus:ring-error-500 shadow-soft hover:shadow-soft-lg',
  };

  const widthStyles = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${widthStyles} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner size="sm" className="mr-2" />}
      {children}
    </button>
  );
}
